import React, { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import { Bold, Italic, Underline, List, ListOrdered, Heading2, Heading3, Pilcrow, Undo2, Redo2, Code2, Type } from 'lucide-react';

// Editor a video dei documenti legali: il testo si modifica come in un programma di
// scrittura e si salva come HTML. I segnaposto {{chiave}} restano testo normale e si
// inseriscono nel punto del cursore. La sanificazione vera la fa il server alla
// pubblicazione; per chi preferisce resta la modalità codice HTML.

export interface LegalTextEditorHandle {
    insertText: (text: string) => void;
}

interface Props {
    value: string;
    onChange: (html: string) => void;
}

const LegalTextEditor = forwardRef<LegalTextEditorHandle, Props>(({ value, onChange }, ref) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const rangeRef = useRef<Range | null>(null);
    const [codice, setCodice] = useState(false);

    // Il contenuto si scrive nel div solo quando arriva da fuori (apertura, modalità codice):
    // riscriverlo a ogni battuta sposterebbe il cursore.
    useEffect(() => {
        const el = editorRef.current;
        if (el && !codice && el.innerHTML !== value) el.innerHTML = value || '';
    }, [value, codice]);

    const emit = () => { if (editorRef.current) onChange(editorRef.current.innerHTML); };

    const salvaSelezione = () => {
        const sel = window.getSelection();
        const el = editorRef.current;
        if (sel && sel.rangeCount > 0 && el && el.contains(sel.anchorNode)) {
            rangeRef.current = sel.getRangeAt(0).cloneRange();
        }
    };

    const ripristinaSelezione = () => {
        const el = editorRef.current;
        if (!el) return;
        el.focus();
        const sel = window.getSelection();
        if (!sel) return;
        sel.removeAllRanges();
        if (rangeRef.current) {
            sel.addRange(rangeRef.current);
        } else {
            const r = document.createRange();
            r.selectNodeContents(el);
            r.collapse(false);
            sel.addRange(r);
        }
    };

    const cmd = (command: string, arg?: string) => {
        ripristinaSelezione();
        document.execCommand(command, false, arg);
        salvaSelezione();
        emit();
    };

    useImperativeHandle(ref, () => ({
        insertText: (text: string) => {
            if (codice) {
                onChange(`${value}${text}`);
                return;
            }
            cmd('insertText', text);
        },
    }));

    const btn = 'p-1.5 rounded hover:bg-gray-200 text-gray-600';
    // mousedown con preventDefault: il clic sul pulsante non toglie la selezione dal testo.
    const B: React.FC<{ title: string; onClick: () => void; children: React.ReactNode }> = ({ title, onClick, children }) => (
        <button type="button" title={title} className={btn} onMouseDown={e => { e.preventDefault(); onClick(); }}>{children}</button>
    );

    return (
        <div className="border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500">
            <div className="flex flex-wrap items-center gap-0.5 px-2 py-1 border-b bg-gray-50">
                {!codice && (
                    <>
                        <B title="Titolo" onClick={() => cmd('formatBlock', '<h2>')}><Heading2 size={16} /></B>
                        <B title="Sottotitolo" onClick={() => cmd('formatBlock', '<h3>')}><Heading3 size={16} /></B>
                        <B title="Paragrafo normale" onClick={() => cmd('formatBlock', '<p>')}><Pilcrow size={16} /></B>
                        <span className="w-px h-5 bg-gray-300 mx-1" />
                        <B title="Grassetto" onClick={() => cmd('bold')}><Bold size={16} /></B>
                        <B title="Corsivo" onClick={() => cmd('italic')}><Italic size={16} /></B>
                        <B title="Sottolineato" onClick={() => cmd('underline')}><Underline size={16} /></B>
                        <span className="w-px h-5 bg-gray-300 mx-1" />
                        <B title="Elenco puntato" onClick={() => cmd('insertUnorderedList')}><List size={16} /></B>
                        <B title="Elenco numerato" onClick={() => cmd('insertOrderedList')}><ListOrdered size={16} /></B>
                        <span className="w-px h-5 bg-gray-300 mx-1" />
                        <B title="Annulla" onClick={() => cmd('undo')}><Undo2 size={16} /></B>
                        <B title="Ripeti" onClick={() => cmd('redo')}><Redo2 size={16} /></B>
                    </>
                )}
                <button
                    type="button"
                    onClick={() => { rangeRef.current = null; setCodice(c => !c); }}
                    className="ml-auto inline-flex items-center gap-1 text-xs px-2 py-1 rounded text-gray-500 hover:bg-gray-200"
                    title={codice ? 'Torna alla modifica a video' : 'Modifica il codice HTML'}
                >
                    {codice ? <><Type size={14} /> Modifica a video</> : <><Code2 size={14} /> HTML</>}
                </button>
            </div>
            {codice ? (
                <textarea
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    rows={18}
                    className="w-full p-3 outline-none font-mono text-xs block"
                />
            ) : (
                <div
                    ref={editorRef}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={emit}
                    onKeyUp={salvaSelezione}
                    onMouseUp={salvaSelezione}
                    onBlur={salvaSelezione}
                    className="p-4 min-h-[320px] max-h-[55vh] overflow-y-auto outline-none text-sm text-gray-800
                        [&_h1]:text-xl [&_h1]:font-bold [&_h1]:mb-3
                        [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mt-4 [&_h2]:mb-2
                        [&_h3]:font-bold [&_h3]:mt-3 [&_h3]:mb-1
                        [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:mb-1
                        [&_table]:border-collapse [&_td]:border [&_td]:p-1 [&_th]:border [&_th]:p-1 [&_a]:text-indigo-600 [&_a]:underline"
                />
            )}
        </div>
    );
});

export default LegalTextEditor;
